"use client";

import * as React from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { AddToCartButton, EcommerceProductsProvider, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function DailyGrindsCarousel() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  const scrollerRef = React.useRef<HTMLDivElement | null>(null);
  const scrollBy = (dir: "prev" | "next") => {
    const el = scrollerRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.8;
    el.scrollBy({
      left: dir === "next" ? amount : -amount,
      behavior: "smooth"
    });
  };
  return <>
      {}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-6 items-end mb-12 md:mb-16">
        <div className="md:col-span-9">
          <div className="font-default text-xs sm:text-sm uppercase tracking-[0.18em] text-emerald-900 mb-6 flex items-center gap-3">
            <span>MISSION 01</span>
            <span className="inline-block w-1.5 h-1.5 bg-emerald-900" />
            <span>CURRENT CAUSES</span>
          </div>
          <h2 className="font-default font-semibold uppercase text-emerald-950 leading-[0.92] tracking-[-0.03em] text-[clamp(2.5rem,5.5vw,5rem)]">
            OUR{" "}
            <span className="inline-block bg-emerald-100 text-emerald-900 border-[3px] border-emerald-900 px-3 py-1 -rotate-[1.5deg]">
              ACTIVE
            </span>{" "}
            CAUSES
          </h2>
        </div>
        <div className="md:col-span-3 flex md:flex-col md:items-end gap-4 items-center justify-between md:justify-end">
          <span className="font-default text-xs uppercase tracking-[0.18em] text-emerald-900">
            SCROLL &rarr;
          </span>
          <div className="flex gap-3">
            <Button type="button" variant="ghost" size="icon" onClick={() => scrollBy("prev")} aria-label="Previous" className="size-12 rounded-lg border-[3px] border-emerald-900 bg-transparent text-emerald-900 hover:bg-emerald-900 hover:text-amber-50 hover:shadow-lg hover:translate-x-1 hover:translate-y-1 transition-all duration-150">
              <ArrowLeft className="size-5" />
            </Button>
            <Button type="button" variant="ghost" size="icon" onClick={() => scrollBy("next")} aria-label="Next" className="size-12 rounded-lg border-[3px] border-emerald-900 bg-transparent text-emerald-900 hover:bg-emerald-900 hover:text-amber-50 hover:shadow-lg hover:translate-x-1 hover:translate-y-1 transition-all duration-150">
              <ArrowRight className="size-5" />
            </Button>
          </div>
        </div>
      </div>

      {}
      {loading ? <div className="flex gap-6 overflow-hidden">
          {[...Array(4)].map((_, i) => <div key={i} data-index={i} className="shrink-0 w-[280px] sm:w-[320px] md:w-[360px] h-[520px] bg-emerald-50 border-[3px] border-emerald-900 animate-pulse" />)}
        </div> : error ? <p className="font-default text-sm text-emerald-900">Failed to load causes.</p> : products.length === 0 ? <p className="font-default text-sm text-emerald-900">No active causes to show yet.</p> : <div ref={scrollerRef} className="flex gap-6 md:gap-8 overflow-x-auto snap-x snap-mandatory pb-6 -mx-4 px-4 md:-mx-0 md:px-0 scroll-smooth" style={{
      scrollbarWidth: "thin"
    }}>
          {products.map((product, i) => <article data-index={i} key={product.id} className="group shrink-0 snap-start w-[280px] sm:w-[320px] md:w-[360px] bg-amber-50 text-emerald-950 border-[3px] border-emerald-900 hover:shadow-lg hover:translate-x-1 hover:translate-y-1 transition-all duration-150 flex flex-col rounded-lg">
              {}
              <Link to={product.link} className="block">
                <div className="relative aspect-square overflow-hidden border-b-[3px] border-emerald-900 bg-emerald-100">
                  {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} className="w-full h-full object-cover" loading="lazy" decoding="async" /> : <div className="w-full h-full bg-emerald-100" />}
                </div>
              </Link>

              {}
              <div className="p-5 md:p-6 flex flex-col gap-3 flex-1">
                <div className="flex items-center justify-between font-default text-[0.7rem] uppercase tracking-[0.18em] text-emerald-800">
                  <span>ID / {String(product.id).padStart(4, "0")}</span>
                  <span className="inline-block w-1.5 h-1.5 bg-emerald-600" />
                </div>

                <Link to={product.link}>
                  <h3 className="font-default font-semibold uppercase text-emerald-950 tracking-[-0.02em] leading-[1.0] text-[clamp(1.25rem,1.8vw,1.75rem)] hover:underline underline-offset-4 decoration-[3px]">
                    {product.title}
                  </h3>
                </Link>

                {product.short_description && <div className="font-default text-sm text-emerald-900/80 leading-[1.5] [&_p]:mb-0 [&_p:last-child]:mb-0 line-clamp-3" dangerouslySetInnerHTML={{
            __html: product.short_description
          }} />}

                <div className="mt-auto pt-3 flex items-center justify-between gap-3">
                  <div className="relative inline-block">
                    <span aria-hidden="true" className="absolute inset-0 -m-1 bg-emerald-200 border-[3px] border-emerald-900 -rotate-[1.5deg]" />
                    <span className="relative font-default font-semibold text-emerald-950 text-xl md:text-2xl px-2">
                      ${Number(product.price).toFixed(2)}
                    </span>
                  </div>

                  <AddToCartButton productId={product.id} variant="default" size="default" text="SUPPORT" showQuantity={false} buttonClassName="bg-emerald-700 text-white font-default font-semibold uppercase tracking-wider border-[3px] border-emerald-900 rounded-lg shadow-md hover:shadow-lg hover:translate-x-[3px] hover:translate-y-[3px] transition-all duration-150" />
                </div>
              </div>
            </article>)}
        </div>}
    </>;
}
export default function FeaturedCauses() {
  const wp_query_params = {
    per_page: 8,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="1530" id="featured-causes-this-month" className="bg-amber-50 text-emerald-950 relative overflow-hidden py-24 md:py-32 border-t-4 border-b-4 border-emerald-900" style={{
    backgroundImage: "radial-gradient(circle, hsl(150 25% 45% / 0.1) 1.5px, transparent 1.5px)",
    backgroundSize: "12px 12px"
  }}>
      <div className="relative max-w-[1440px] mx-auto px-5 sm:px-8 md:px-12">
        <EcommerceProductsProvider wp_query={{
        per_page: 8,
        order: "desc",
        orderby: "date"
      }}>
          <DailyGrindsCarousel />
        </EcommerceProductsProvider>
      </div>
    </section>;
}